/**
 * Low-level cookie primitives shared by every Privisy cross-subdomain cookie
 * (scan-intent, scan-intent-id, unlock intent, tracking consent).
 *
 * These live in their own module rather than in `index.ts` so the modules that
 * need them can layer cleanly: `cookies -> tracking -> index`.
 * `index.ts` re-exports the public surface, so nothing about the package's API
 * changes by them living here.
 */

export const COOKIE_TTL_SECONDS = 86400; // 24 hours

function rootDomainFromEnv(): string | undefined {
  // REACT_APP_* (CRA/dashboard) and NEXT_PUBLIC_* (Next.js/www) must agree so
  // cookies set by www can be cleared by the dashboard and vice-versa.
  return process.env.REACT_APP_ROOT_DOMAIN || process.env.NEXT_PUBLIC_ROOT_DOMAIN || undefined;
}

function currentHostname(): string {
  if (typeof window !== 'undefined' && window.location) return window.location.hostname;
  return '';
}

/**
 * Resolve the value of the cookie `domain=` attribute for the cross-subdomain
 * intent cookies. Returns '' (meaning: omit the attribute, host-only cookie)
 * for localhost, IPs, and single-label hosts.
 *
 * Why this exists: the intent cookies are written by www.getprivisy.com and
 * must be readable by scanner.getprivisy.com so a claim can be recovered after
 * the sign-up email-verification redirect (which lands on a clean URL with no
 * ?claimScanId=). For that the cookie must be scoped to the registrable parent
 * (`.getprivisy.com`), NOT host-only on `www.`.
 *
 * The ROOT_DOMAIN env var is authoritative when set to a real domain. When it
 * is absent or left at the dev default ('localhost'), we derive the registrable
 * parent from the current hostname. Previously a missing/blank env var in a
 * production build silently fell back to 'localhost', which scoped the cookie
 * host-only and broke the www -> scanner claim handoff.
 *
 * Kept pure (no globals) so the fallback contract is directly testable.
 */
export function resolveCookieDomain(hostname: string, envDomain?: string): string {
  const env = (envDomain || '').trim().replace(/^\./, '');
  let root = '';

  if (env && env !== 'localhost') {
    root = env;
  } else {
    const host = (hostname || '').trim().toLowerCase().replace(/\.$/, '');
    const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':');
    const labels = host.split('.').filter(Boolean);
    // Host-only for localhost, IP literals, and single-label hosts — there is
    // no registrable parent to share across.
    if (host && host !== 'localhost' && !isIp && labels.length >= 2) {
      root = labels.slice(-2).join('.');
    }
  }

  return root ? `.${root}` : '';
}

function cookieDomainAttr(): string {
  return resolveCookieDomain(currentHostname(), rootDomainFromEnv());
}

export function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;
  const domain = cookieDomainAttr();
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
}

export function clearCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const domain = cookieDomainAttr();
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${domain ? `; domain=${domain}` : ''}`;
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

/**
 * The unlock intent (#1779): a signed-out visitor clicked "Unlock this report"
 * on their locked full audit, and sign-up is in the way. Holds the scan id
 * only. Once the claim that follows sign-up resolves for that same scan, the
 * dashboard starts the unlock checkout; any other claim ignores it.
 *
 * A cookie, not sessionStorage: the Clerk email-verification link can open in
 * a new tab, which starts with empty session storage. One hour, so an
 * abandoned sign-up never opens Stripe on a later visit.
 */
export const UNLOCK_INTENT_COOKIE = 'privisy_unlock_intent';
export const UNLOCK_INTENT_TTL_SECONDS = 3600;

export const setUnlockIntent = (scanId: string) =>
  setCookie(UNLOCK_INTENT_COOKIE, encodeURIComponent(scanId), UNLOCK_INTENT_TTL_SECONDS);
export const getUnlockIntent = (): string | null => {
  const raw = readCookie(UNLOCK_INTENT_COOKIE);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
};
export const clearUnlockIntent = () => clearCookie(UNLOCK_INTENT_COOKIE);
